import { Link } from 'react-router-dom';
import { MdAdd, MdArrowDownward, MdArrowUpward, MdInfoOutline, MdOpenInNew, MdRefresh } from 'react-icons/md';
import TextEdit from '../../../../molecules/TextEdit';
import SectionCard from '../../../communication/composition/shared/SectionCard';
import CollectionRuleRow, { RuleGridHeader } from './CollectionRuleRow';
import PhaseScheduleFields from './PhaseScheduleFields';
import useCollectionCompositions from '../useCollectionCompositions';
import type { FieldError } from '../../../../../services/collectionApi';
import {
    CollectionPhase,
    CollectionPolicy,
    CollectionRule,
    DEFAULT_OVERDUE_SCHEDULE,
    DEFAULT_PREVENTIVE_SCHEDULE,
    newId,
    PHASE_LABEL,
    PolicySchedules,
} from '../collectionModel';

interface CollectionPolicyEditorProps {
    policy: CollectionPolicy;
    fieldErrors: FieldError[];
    onChange: (policy: CollectionPolicy) => void;
}

const errorFor = (fieldErrors: FieldError[], field: string): string | undefined =>
    fieldErrors.find(e => e.fieldName === field)?.message;

const newRule = (phase: CollectionPhase): CollectionRule => ({
    id: newId('rule'),
    phase,
    channel: 'EMAIL',
    obligorRole: 'PAYER',
    criteria: { fromDaysPastDue: phase === 'OVERDUE' ? 1 : null, assignorCount: null, receivingAccountKind: null, paymentInstruction: null },
    messageCompositionId: null,
    skip: false,
});

const PHASE_DESCRIPTION: Record<CollectionPhase, string> = {
    PREVENTIVE: 'Lembretes enviados antes de a fatura vencer.',
    OVERDUE: 'Cobranças enviadas depois do vencimento, conforme os dias de atraso.',
};

const PHASE_ICON: Record<CollectionPhase, JSX.Element> = {
    PREVENTIVE: <MdArrowUpward size={18} className="text-blue-500" />,
    OVERDUE: <MdArrowDownward size={18} className="text-red-500" />,
};

const RulePriorityNotice = () => (
    <div role="note" className="mb-4 flex gap-3 border-t-4 border-blue-300 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-800 dark:bg-gray-800 dark:text-blue-300">
        <MdInfoOutline size={18} className="mt-0.5 shrink-0" />
        <div>
            <p className="font-semibold">Qual regra vale em cada envio</p>
            <ol className="mt-1 list-decimal space-y-0.5 pl-5">
                <li>Para cada fatura, canal e destinatário (Para quem), entram só as regras cujos critérios batem com a fatura. Critério em Qualquer bate com todos os casos.</li>
                <li>Entre as que batem, vence a que tem mais critérios preenchidos.</li>
                <li>No empate, vence a de maior Atraso desde.</li>
                <li>Se a regra que venceu for Não enviar, esse destinatário não recebe a cobrança.</li>
                <li>Cedente e avalista só recebem quando o acordo da dívida tem essa parte cobrável ativa. Exceções do acordo prevalecem sobre as regras.</li>
            </ol>
        </div>
    </div>
);

const withDefaultSchedule = (schedules: PolicySchedules, phase: CollectionPhase): PolicySchedules => {
    if (phase === 'PREVENTIVE') return { ...schedules, preventive: schedules.preventive ?? { ...DEFAULT_PREVENTIVE_SCHEDULE } };
    return { ...schedules, overdue: schedules.overdue ?? { ...DEFAULT_OVERDUE_SCHEDULE } };
};

const RulesSection = ({ phase, policy, options, fieldErrors, onChange }: {
    phase: CollectionPhase;
    policy: CollectionPolicy;
    options: ReturnType<typeof useCollectionCompositions>['options'];
    fieldErrors: FieldError[];
    onChange: (policy: CollectionPolicy) => void;
}) => {
    const { rules, schedules } = policy;
    const phaseRules = rules.filter(r => r.phase === phase);
    const setRules = (next: CollectionRule[]) => onChange({ ...policy, rules: next });
    const update = (rule: CollectionRule) => setRules(rules.map(r => (r.id === rule.id ? rule : r)));
    const add = () => onChange({ ...policy, rules: [...rules, newRule(phase)], schedules: withDefaultSchedule(schedules, phase) });
    const shownSchedules = phaseRules.length ? withDefaultSchedule(schedules, phase) : schedules;
    const count = phaseRules.length;

    return (
        <SectionCard title={`${PHASE_LABEL[phase]} · ${count} ${count === 1 ? 'regra' : 'regras'}`}
            description={PHASE_DESCRIPTION[phase]} icon={PHASE_ICON[phase]}>
            {phase === 'PREVENTIVE' && <RulePriorityNotice />}
            {count === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center dark:border-slate-600">
                    <p className="text-sm text-gray-500">Nenhuma regra: nada é enviado nesta fase.</p>
                    <button type="button" onClick={add} className="mt-2 text-sm font-medium text-blue-600 hover:underline">Adicionar a primeira regra</button>
                </div>
            ) : (
                <>
                <PhaseScheduleFields phase={phase} preventive={shownSchedules.preventive} overdue={shownSchedules.overdue}
                    fieldErrors={fieldErrors}
                    onPreventiveChange={preventive => onChange({ ...policy, schedules: { ...schedules, preventive } })}
                    onOverdueChange={overdue => onChange({ ...policy, schedules: { ...schedules, overdue } })} />
                <div className="overflow-x-auto">
                    <div className="min-w-[1000px]">
                        <RuleGridHeader phase={phase} />
                        <div className="divide-y divide-gray-100 dark:divide-slate-700">
                            {phaseRules.map(rule => (
                                <CollectionRuleRow key={rule.id} rule={rule} options={options} onChange={update}
                                    onRemove={() => setRules(rules.filter(r => r.id !== rule.id))} />
                            ))}
                        </div>
                    </div>
                </div>
                <button type="button" onClick={add}
                    className="mt-3 inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:text-gray-200 dark:hover:bg-slate-700">
                    <MdAdd size={14} /> Adicionar regra
                </button>
                </>
            )}
        </SectionCard>
    );
};

const CompositionsStatus = ({ compositions }: { compositions: ReturnType<typeof useCollectionCompositions> }) => {
    const active = compositions.options.filter(o => o.active).length;
    const message = compositions.failed
        ? 'Não foi possível carregar as composições.'
        : `${active} ${active === 1 ? 'composição ativa disponível' : 'composições ativas disponíveis'}.`;
    return (
        <div className={`flex flex-wrap items-center gap-3 rounded-lg px-4 py-3 text-xs ${
            compositions.failed || active === 0
                ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                : 'bg-slate-50 text-gray-600 dark:bg-slate-800/60 dark:text-gray-300'
        }`}>
            <MdInfoOutline size={16} className="shrink-0" />
            <span className="flex-1 min-w-[240px]">
                {message}
            </span>
            <button type="button" onClick={compositions.reload} disabled={compositions.loading}
                className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline disabled:opacity-50">
                <MdRefresh size={14} className={compositions.loading ? 'animate-spin' : ''} /> Atualizar
            </button>
            <Link to="/communication/message-composition/new" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline">
                <MdOpenInNew size={14} /> Nova composição da mensagem
            </Link>
        </div>
    );
};

const CollectionPolicyEditor = ({ policy: draft, fieldErrors, onChange: setDraft }: CollectionPolicyEditorProps) => {
    const compositions = useCollectionCompositions();
    const rulesError = errorFor(fieldErrors, 'rules');

    return (
        <div className="space-y-5">
            <SectionCard title="Dados da política" description="Como a política aparece na escolha do acordo de cobrança. Cada fase com regras tem o seu calendário.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_2fr]">
                    <TextEdit label="Nome da política" value={draft.name} errorMessage={errorFor(fieldErrors, 'name')}
                        onChange={e => setDraft({ ...draft, name: e.target.value })} />
                    <TextEdit label="Descrição" value={draft.description} errorMessage={errorFor(fieldErrors, 'description')}
                        onChange={e => setDraft({ ...draft, description: e.target.value })} />
                </div>
            </SectionCard>

            {rulesError && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">{rulesError}</p>}

            <CompositionsStatus compositions={compositions} />

            <RulesSection phase="PREVENTIVE" policy={draft} options={compositions.options} fieldErrors={fieldErrors} onChange={setDraft} />
            <RulesSection phase="OVERDUE" policy={draft} options={compositions.options} fieldErrors={fieldErrors} onChange={setDraft} />
        </div>
    );
};

export default CollectionPolicyEditor;
